import { IpfError } from '@ipfabric/frontend-shared-assets/utils/IpfError';
import { isFeatureFlagEnabled } from '@ipfabric/ui-components/utils/featureFlags';
import {
  type AlternativeTableEndpointOptions,
  type TableEndpoint,
  createAlternativeTableEndpoint,
  createTableEndpointFromOas,
} from '@ipfabric/utils';
import { prop } from '@ipfabric/utils/fp/prop';
import { isOasOperationTable } from '@ipfabric/utils/oas/OasOperationTable';
import { useGlobalStateContextSelectors } from 'components/GlobalState/GlobalStateModel';
import { useMemo } from 'react';

interface Props {
  apiEndpoint: string;
  alternative?:
    | AlternativeTableEndpointOptions<any>
    | ((endpoint: TableEndpoint<any>) => AlternativeTableEndpointOptions<any>);
}

export const useTableEndpoint = (props: Props): TableEndpoint<any> => {
  const globalCtxProps = useGlobalStateContextSelectors({
    oas: prop('oas'),
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies(props.alternative): (historical reason) allow writing simple lambdas in userland
  return useMemo(() => {
    const oasOperationTable =
      globalCtxProps.oas?.paths?.[props.apiEndpoint]?.post;

    if (!isOasOperationTable(oasOperationTable)) {
      throw new IpfError(
        'GENERIC',
        `Table ${props.apiEndpoint} not found in OAS`,
      );
    }

    const tableEndpoint = createTableEndpointFromOas(
      props.apiEndpoint,
      oasOperationTable,
      isFeatureFlagEnabled('ENABLE_NEW_CLOUD_MODEL'),
    );

    if (props.alternative) {
      const alternative =
        typeof props.alternative === 'function'
          ? props.alternative(tableEndpoint)
          : props.alternative;
      return createAlternativeTableEndpoint(tableEndpoint, alternative);
    }
    return tableEndpoint;
  }, [globalCtxProps.oas, props.apiEndpoint]);
};
